'use client'

import React, { useEffect, useState } from "react";
import AlertTooltip from "@/components/alert-tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { InfoIcon, RotateCcw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { FormikProps } from 'formik';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND_OPTION_NEW, DATA_COLLECTION_PRIMARY_PACKAGE_MATERIAL_OPTIONS } from "@/lib/packageOptions";

interface PrimaryPackagingDetailsProps {
  formik: FormikProps<any>;
}

const PrimaryPackagingDetails: React.FC<PrimaryPackagingDetailsProps> = ({ formik,setHasUnsavedChanges }: { formik: any }) => {
  const [isFilledWeightAutoCalculated, setIsFilledWeightAutoCalculated] = useState(true);

  const fields = [
    "packageType",
    "category",
    "brand",
    "application",
    "finishType",
    "packageMaterial",
    "filledBottleTopLoad",
    "diameter",
    "packageHeight",
    "netContent",
    "filledWeight",
    "bottleDesign",
    "canDesign"
  ];

  const packageTypeOptions = [
    { label: "Bottle", value: "bottle" },
    { label: "Can", value: "can" }
  ];

  const categoryOptions = [
    "CSD",
    "Tea",
    "S&F",
    "Hydration",
    "juice plus",
    "Coffee Partnership"
  ];

  const applicationOptions = ["Ambient", "Aseptic", "Concentrates and Syrups", "CSD", "Cold Fill", "ESL", "gel", "Hot Fill",  "NCB Non-Pressurized", "NCB Pressurized", "Nitrogen Dosed Hot Fill", "Not Applicable","Powder", "Retort", "Tunnel Pasteurization" ];

  const finishTypeOptions = [
    "28mm", "26mm", "38mm", "33mm", "43mm", "52mm", "37mm", "26.7mm", "26mm1881", "28mm1881",
    "28mm1810", "38mmJared", "97mm", "38mmDWC", "43mmVHS", "43mmDWC", "48mm", "29mm", "25mm",
    "100SP400", "110SP400", "120mm", "Pull Tab Strip", "Gable Top", "120mmSP400", "31mm", "51mm", "Other"
  ];

  const packageMaterialOptions = DATA_COLLECTION_PRIMARY_PACKAGE_MATERIAL_OPTIONS;

  const bottleDesignOptions = [
    "Global Swirl",
    "Prius",
    "McCartney",
    "Citrus",
    "4 Bolt",
    "Azera",
    "Ecofina",
    "Ecofina - Gen2",
    "Propel",
    "Sidekick",
    "Ripples",
    "Axl",
    "Skinnee",
    "Straight Wall",
    "Vuitton",
    "Wolverine",
    "Live Big",
    "Lizardade",
    "Kenny",
    "Gatorade",
    "Tropicana",
    "LIT Chilled",
    "Naked",
    "TPP Carafe",
    "Aquafina",
    "ColdBrew",
    "Polar",
    "Rho",
    "Active",
    "Kevita",
    "LifeWtr"
  ];

  const canDesignOptions = [
    // "Rexam",
    // "Rockstar",
    // 'Canpack',
    // "Ball",
    // 'Ardagh',
    // "Crown",
    // 'Metal Container Corp',
    // "Latham",
    // "Tulsa",
    // "MCC",
    // "Louis",
    // "Refresco"
    "Canpack", "Crown", "Ball", "Ardagh", "Metal Container Corp"
  ];

  const isBottle = formik.values.primaryPackagingDetails.packageType === "bottle";
  const isCan = formik.values.primaryPackagingDetails.packageType === "can";

  const applicationValue = formik.values.primaryPackagingDetails.application;
  const selectedApplication = typeof applicationValue === "string"
    ? applicationOptions.find((option) => option.toLowerCase() === applicationValue.toLowerCase()) ?? applicationValue
    : applicationValue;

  const brandValue = formik.values.primaryPackagingDetails.brand;
  const selectedBrand = typeof brandValue === "string"
    ? BRAND_OPTION_NEW.find((option) => option.toLowerCase() === brandValue.toLowerCase()) ?? brandValue
    : brandValue;

  const calculateFilledWeight = (): number => {
    const { netContent, netContentUnit, category } = formik.values.primaryPackagingDetails;

    // If any of the required values are missing, return 0
    if (!netContent || !netContentUnit || !category) return 0;

    // Convert netContent to ml if unit is oz
    const netContentMl = netContentUnit === 'oz' ? netContent * 29.5735 : netContent;

    // Define density values for different categories
    const density: { [key: string]: number } = {
      'CSD': 1.016,
      'S&F': 1.027,
      'Coffee Partnership': 1.031,
      'Tea': 1.031,
      'Hydration': 1,
      'juice plus': 1.15
    };

    // Get the density for the given category, defaulting to 1 if not found
    const categoryDensity = density[category] || 1;

    // Calculate the empty bottle weight (you can replace this with actual logic)
    const emptyBottleWeight = 50; // Placeholder, adjust based on your own logic

    // Calculate the filled weight in grams
    const filledWeightGrams = (netContentMl * categoryDensity) + emptyBottleWeight;

    // Convert the weight to pounds (1 gram = 0.00220462 pounds)
    const filledWeightPounds = filledWeightGrams * 0.00220462;

    // Return the filled weight in pounds
    return filledWeightPounds;
  };

  // Add unit conversion functions
  const convertWeight = (value: number, fromUnit: string, toUnit: string): number => {
    // First convert to grams as base unit
    let valueInGrams = value;
    switch (fromUnit) {
      case 'lbs':
        valueInGrams = value * 453.592;
        break;
      case 'oz':
        valueInGrams = value * 28.3495;
        break;
      case 'kg':
        valueInGrams = value * 1000;
        break;
      case 'g':
        valueInGrams = value;
        break;
    }

    // Then convert from grams to target unit
    switch (toUnit) {
      case 'lbs':
        return valueInGrams / 453.592;
      case 'oz':
        return valueInGrams / 28.3495;
      case 'kg':
        return valueInGrams / 1000;
      case 'g':
        return valueInGrams;
      default:
        return value;
    }
  };

  const renderUnitSelect = (field: string, options: string[]) => (
    <Select
      name={`primaryPackagingDetails.${field}Unit`}
      value={formik.values.primaryPackagingDetails[`${field}Unit`]}
      onValueChange={(value) => {
        // Handle unit conversion for auto-calculated fields
        if (field === 'filledWeight') {
          const currentValue = formik.values.primaryPackagingDetails.filledWeight;
          const currentUnit = formik.values.primaryPackagingDetails.filledWeightUnit;
          const convertedValue = convertWeight(currentValue, currentUnit, value);
          formik.setFieldValue('primaryPackagingDetails.filledWeight', convertedValue.toFixed(2));
        }
        formik.setFieldValue(`primaryPackagingDetails.${field}Unit`, value);
      }}
    >
      <SelectTrigger className="w-[70px] h-6 text-xs bg-unitSelect rounded-[11px] py-3 rounded-[11px] py-3 text-white">
        <SelectValue placeholder="Unit" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option} value={option}>{option}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const calculateEmptyBottleWeight = (netContent: number) => {
    if (netContent > 3000) return 95;
    if (netContent > 2000) return 70;
    if (netContent > 1000) return 55;
    if (netContent > 700) return 45;
    if (netContent > 500) return 35;
    return 25;
  };

  useEffect(() => {
    if(formik.values.primaryPackagingDetails.isFilledWeightAutoCalculated){
      const calculatedFilledWeight = calculateFilledWeight();
      formik.setFieldValue('primaryPackagingDetails.filledWeight', calculatedFilledWeight.toFixed(2));
      // setIsFilledWeightAutoCalculated(true);
    } 
  }, [formik.values.primaryPackagingDetails.netContent, formik.values.primaryPackagingDetails.netContentUnit, formik.values.primaryPackagingDetails.category]);

  const handleFilledWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    formik.setFieldValue('primaryPackagingDetails.isFilledWeightAutoCalculated', false);
    // setIsFilledWeightAutoCalculated(false);
  };

  return (
    <div className="w-full">
      <div className="w-full pt-5">
        {/* Project Details 1st Row */}
        <div className="flex-center border-border-style-1 border w-full">
          {["Package Type*", "Category*", "Brand*", "Application*", ...(isBottle ? ["Finish type*"] : [])].map((label) => (
            <div className="label-bg" key={label}>
              <Label className="label lg:text-sm text-[9px] font-bold">{label}</Label>
            </div>
          ))}
        </div>

        <div className="flex-center border-border-style-1 border w-full">
          {fields.slice(0, (isBottle) ? 5 : 4).map((field) => (
            <div className="relative w-full" key={field}>
              {field === "packageType" && (
                <Select
                  name={`primaryPackagingDetails.${field}`}
                  value={formik.values.primaryPackagingDetails[field]}
                  onValueChange={(value) => formik.setFieldValue(`primaryPackagingDetails.${field}`, value)}
                >
                  <SelectTrigger className="w-full border-2 border-required bg-[#EDEEFF]">
                    <SelectValue placeholder="Select Package Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {packageTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {field === "category" && (
                <Select
                  name={`primaryPackagingDetails.${field}`}
                  value={formik.values.primaryPackagingDetails[field]}
                  onValueChange={(value) => formik.setFieldValue(`primaryPackagingDetails.${field}`, value)}
                >
                  <SelectTrigger className="w-full  border-2 border-required bg-[#EDEEFF]">
                    <SelectValue placeholder="Select Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {field === "brand" && (
                <Select
                  name={`primaryPackagingDetails.${field}`}
                  // value={formik.values.primaryPackagingDetails[field]}
                  value={selectedBrand}
                  onValueChange={(value) => formik.setFieldValue(`primaryPackagingDetails.${field}`, value)}
                >
                  <SelectTrigger className=" border-2 border-required bg-[#EDEEFF]">
                    <SelectValue placeholder="Select Brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {BRAND_OPTION_NEW.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {field === "application" && (
                <Select
                  name={`primaryPackagingDetails.${field}`}
                  // value={formik.values.primaryPackagingDetails[field]}
                  value={selectedApplication}
                  onValueChange={(value) => formik.setFieldValue(`primaryPackagingDetails.${field}`, value)}
                >
                  <SelectTrigger className=" border-2 border-required bg-[#EDEEFF]">
                    <SelectValue placeholder="Select Application" />
                  </SelectTrigger>
                  <SelectContent>
                    {applicationOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {field === "finishType" && isBottle && (
                <Select
                  name={`primaryPackagingDetails.${field}`}
                  value={formik.values.primaryPackagingDetails[field]}
                  onValueChange={(value) => formik.setFieldValue(`primaryPackagingDetails.${field}`, value)}
                >
                  <SelectTrigger className=" border-2 border-required bg-[#EDEEFF]">
                    <SelectValue placeholder="Select Finish Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {finishTypeOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {formik.touched.primaryPackagingDetails?.[field] &&
                formik.errors.primaryPackagingDetails?.[field] && (
                  <AlertTooltip
                    message={formik.errors.primaryPackagingDetails[field] as string}
                  />
                )}
            </div>
          ))}
        </div>

        {/* Project Details 2nd Row */}
        <div className="flex-center border-border-style-1 border w-full">
          {[
            "Package Material*",
            "Filled bottle Top Load (lb - Force)",
            "Diameter*",
            "Package Height*",
          ].map((label) => (
            <div className="label-bg flex items-center" key={label}>
              <Label className="label lg:text-sm text-[9px] font-bold flex-grow">{label}</Label>
              {label === "Diameter*" && renderUnitSelect("diameter", ["inch", "mm"])}
              {label === "Package Height*" && renderUnitSelect("packageHeight", ["inch", "mm"])}
            </div>
          ))}
        </div>

        <div className="flex-center border-border-style-1 border w-full">
          {fields.slice(5, 9).map((field) => (
            <div className="relative w-full" key={field}>
              {field === "packageMaterial" ? (
                <Select
                  name={`primaryPackagingDetails.${field}`}
                  value={formik.values.primaryPackagingDetails[field]}
                  onValueChange={(value) => formik.setFieldValue(`primaryPackagingDetails.${field}`, value)}
                >
                  <SelectTrigger className=" border-2 border-required bg-[#EDEEFF]">
                    <SelectValue placeholder="Select Package Material" />
                  </SelectTrigger>
                  <SelectContent>
                    {packageMaterialOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : 
              field==='filledBottleTopLoad'?
              (
                <Input
                  name={`primaryPackagingDetails.${field}`}
                  placeholder="Enter"
                  type="number"
                  value={formik.values.primaryPackagingDetails[field]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={''}
                />
              ):
              (
                <Input
                  name={`primaryPackagingDetails.${field}`}
                  placeholder="Enter"
                  type="number"
                  value={formik.values.primaryPackagingDetails[field]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={'border-2 border-required'}
                />
              )}
              {formik.touched.primaryPackagingDetails?.[field] &&
                formik.errors.primaryPackagingDetails?.[field] && (
                  <AlertTooltip
                    message={formik.errors.primaryPackagingDetails[field] as string}
                  />
                )}
            </div>
          ))}
        </div>

        {/* Project Details 3rd Row */}
        <div className={`flex-center border-border-style-1 border w-full`}>
          {["Net Content*", "Filled Weight*", ...(isBottle ? ["Bottle Design"] : isCan ? ["Can supplier"] : [])].map(
            (label) => (
              <div
                className={`label-bg ${(isBottle || isCan) ? 'w-1/3' : 'w-1/2'} flex items-center`}
                key={label}
              >
                <Label className="label lg:text-sm text-[9px] font-bold flex-grow flex items-center">
                  {label}
                  {label === "Filled Weight*" && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Formula: (Net Content(ml) * Density) + EmptyBottleWeight(gms)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </Label>
                {label === "Net Content*" && renderUnitSelect("netContent", ["oz", "ml"])}
                {label === "Filled Weight*" && (
                  <div className="flex items-center gap-1">
                    {!formik.values.primaryPackagingDetails.isFilledWeightAutoCalculated && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => {
                                const calculatedValue = calculateFilledWeight();
                                formik.setFieldValue('primaryPackagingDetails.filledWeight', calculatedValue.toFixed(2));
                                formik.setFieldValue('primaryPackagingDetails.isFilledWeightAutoCalculated', true);
                                setHasUnsavedChanges(true)
                                // setIsFilledWeightAutoCalculated(true);
                              }}
                              className="p-1 hover:bg-gray-100 rounded-full"
                            >
                              <RotateCcw className="h-3 w-3 text-gray-500" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Reset to auto-calculated value</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {renderUnitSelect("filledWeight", ["lbs", "oz", 'g', 'kg'])}
                  </div>
                )}
              </div>
            )
          )}
        </div>

        <div className="flex-center border-border-style-1 border w-full">
          {["netContent", "filledWeight", ...(isBottle ? ["bottleDesign"] : isCan ? ["canDesign"] : [])].map((field) => (
            <div
              className={`relative ${(isBottle || isCan) ? 'w-1/3' : 'w-1/2'}`}
              key={field}
            >
              {field === "filledWeight" ? (
                <Input
                  name={`primaryPackagingDetails.${field}`}
                  placeholder="Enter"
                  type="number"
                  value={formik.values.primaryPackagingDetails[field]}
                  onChange={field === 'filledWeight' ? handleFilledWeightChange : formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={cn(
                    'border-2 border-required',
                    field === 'filledWeight' && formik.values.primaryPackagingDetails.isFilledWeightAutoCalculated && " bg-bgYellow "
                  )}
                />
              ) : field === "bottleDesign" && isBottle ? (
                <Select
                  name={`primaryPackagingDetails.${field}`}
                  value={formik.values.primaryPackagingDetails[field]}
                  onValueChange={(value) => formik.setFieldValue(`primaryPackagingDetails.${field}`, value)}
                >
                  <SelectTrigger className="  bg-[#EDEEFF]">
                    <SelectValue placeholder="Select Bottle Design" />
                  </SelectTrigger>
                  <SelectContent>
                    {bottleDesignOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
                : field === "canDesign" && isCan ? (
                  <Select
                    name={`primaryPackagingDetails.${field}`}
                    value={formik.values.primaryPackagingDetails[field]}
                    onValueChange={(value) => formik.setFieldValue(`primaryPackagingDetails.${field}`, value)}
                  >
                    <SelectTrigger className="  bg-[#EDEEFF]">
                      <SelectValue placeholder="Select Can supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {canDesignOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    name={`primaryPackagingDetails.${field}`}
                    placeholder="Enter"
                    type="number"
                    value={formik.values.primaryPackagingDetails[field]}
                    onChange={field === 'filledWeight' ? handleFilledWeightChange : formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={cn(
                      "border-2 border-required",
                      field === 'filledWeight' && formik.values.primaryPackagingDetails.isFilledWeightAutoCalculated && "bg-bgYellow"
                    )}
                  />
                )}
              {formik.touched.primaryPackagingDetails?.[field] &&
                formik.errors.primaryPackagingDetails?.[field] && (
                  <AlertTooltip
                    message={formik.errors.primaryPackagingDetails[field] as string}
                  />
                )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrimaryPackagingDetails;
